// src/auth/supabase.ts
// SUPABASE AUTH (GoTrue) OVER PLAIN `fetch`. Two endpoints, no SDK, no runtime dependency.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY NOT `@supabase/supabase-js`
// ══════════════════════════════════════════════════════════════════════════════════════════════
// This app has ZERO runtime dependencies and ships 46 kB. `@supabase/supabase-js` is ~120 kB
// minified before gzip — it carries PostgREST, Realtime (a WebSocket client), Storage and Functions
// clients, none of which this app uses: every data read goes through the gateway at `apiBase()`,
// never directly at Postgres. We would be tripling the bundle for two HTTP POSTs.
//
// It also brings a SECOND session store. The SDK persists its session in `localStorage` by default,
// which a service worker cannot read — the exact reason this app keeps the token in IndexedDB. We
// would be running two sources of truth for "am I signed in", and they would disagree the first time
// a token rotated in a background tab.
//
// So: the two GoTrue endpoints this app actually needs, called directly.
//
//   POST {SUPABASE_URL}/auth/v1/token?grant_type=password        {email, password}
//   POST {SUPABASE_URL}/auth/v1/token?grant_type=refresh_token   {refresh_token}
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE ANON KEY IS NOT A SECRET
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The `anon` key is designed to be published in a browser bundle. It identifies the PROJECT; it
// grants nothing on its own, because every table is behind RLS and every gateway route is behind
// `requireAuth`. Building a proxy to "hide" it would add a server, a deploy and a failure mode in
// exchange for hiding a value Supabase prints in its own dashboard under the heading "public".
//
// The SERVICE ROLE key is the secret. It is not in this file, this bundle, or this repository, and
// nothing here should ever put it there.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// NOTHING IN THIS FILE LOGS A TOKEN
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Not the access token, not the refresh token, not the anon key, not the password. Every message
// this module produces is composed from a closed set of codes below. A `console.log(response)` while
// debugging is how a refresh token ends up in a phone's shared crash report.

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * CONFIGURATION — build-time only
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** Set only by tests. Product code never writes this — see the note on the setter. */
let testConfig: { url: string; anonKey: string } | null = null;

function fromEnv(name: string): string | null {
  try {
    const v = (import.meta as unknown as { env?: Record<string, string> }).env?.[name];
    return v && v.trim() ? v.trim() : null;
  } catch {
    return null;
  }
}

export function supabaseUrl(): string | null {
  if (testConfig) return testConfig.url;
  const v = fromEnv('VITE_SUPABASE_URL');
  return v ? v.replace(/\/+$/, '') : null;
}

export function supabaseAnonKey(): string | null {
  if (testConfig) return testConfig.anonKey;
  return fromEnv('VITE_SUPABASE_ANON_KEY');
}

export function supabaseConfigured(): boolean {
  return !!supabaseUrl() && !!supabaseAnonKey();
}

/**
 * WHICH build-time variables are missing, by name.
 *
 * The same discipline the rest of this app applies to a DARK backend: when something is not
 * configured, name the variables rather than showing a broken form. A password box that cannot
 * possibly submit is worse than no password box.
 */
export function missingSupabaseConfig(): string[] {
  const missing: string[] = [];
  if (!supabaseUrl()) missing.push('VITE_SUPABASE_URL');
  if (!supabaseAnonKey()) missing.push('VITE_SUPABASE_ANON_KEY');
  return missing;
}

/**
 * TEST SEAM ONLY. There is deliberately no product path that points this app at a different
 * Supabase project at runtime: the anon key and the project URL must agree, and letting a URL be
 * typed in would let someone aim a real password at a host of their choosing. The API base is
 * overridable (see config.ts) because it carries no credential of its own; this is not.
 */
export function __setSupabaseConfigForTests(url: string | null, anonKey?: string): void {
  testConfig = url === null ? null : { url: url.replace(/\/+$/, ''), anonKey: anonKey ?? 'anon-key-for-tests' };
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * RESULTS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export interface TokenSet {
  readonly accessToken: string;
  /**
   * Null only if GoTrue did not return one. Supabase always does for these two grants; a null here
   * means the session cannot be renewed and the app must say so rather than discover it in an hour.
   */
  readonly refreshToken: string | null;
  /**
   * Epoch ms on THIS DEVICE'S clock: `Date.now() + expires_in * 1000`.
   *
   * Deliberately not the JWT's own `exp`. `exp` is on the server's clock, and a phone whose clock is
   * an hour fast would read a freshly-minted token as already expired and refresh in a loop. A
   * device-relative deadline is immune to skew, and skew on a phone that has been in a bag all day
   * is common. Null when GoTrue sent no usable `expires_in` — in which case this app does not guess
   * an expiry, it just lets the server answer 401 and refreshes then.
   */
  readonly expiresAt: number | null;
  readonly userId: string | null;
  readonly email: string | null;
}

/** The closed set of ways signing in or refreshing can fail. A screen switches on this. */
export type AuthFailureCode =
  /** The email and password do not match an account. */
  | 'invalid_credentials'
  /** The account exists but its email address has never been confirmed. */
  | 'email_not_confirmed'
  /** GoTrue rate-limited us. Retrying later genuinely can work. */
  | 'rate_limited'
  /** A refresh token that GoTrue will not accept — used, revoked, or from another project. */
  | 'invalid_refresh_token'
  /** This build has no Supabase URL / anon key. Nothing can be attempted. */
  | 'not_configured'
  /** The request never reached Supabase. */
  | 'unreachable'
  /** Supabase answered 5xx. */
  | 'server_error'
  /** An answer we could not place. Carries the status so it is visible rather than swallowed. */
  | 'unknown';

export interface AuthFailure {
  readonly ok: false;
  readonly code: AuthFailureCode;
  /** The sentence to put on screen. Composed here — see the note in `describe()`. */
  readonly message: string;
  /** HTTP status, or 0 when the request never reached a server. */
  readonly status: number;
  /**
   * Could trying the SAME thing again later work?
   *
   * This is the single most load-bearing field in the file. On a refresh, `retryable: true` means
   * "do NOT sign this person out" — a coach who walked behind the pavilion must not lose their
   * session because one refresh could not reach the network. `retryable: false` means the
   * credential is genuinely dead and a clean re-login is the honest outcome.
   */
  readonly retryable: boolean;
}

export type AuthResult = { readonly ok: true; readonly tokens: TokenSet } | AuthFailure;

export function isAuthFailure(r: AuthResult): r is AuthFailure {
  return r.ok === false;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE CALLS
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

function asRecord(v: unknown): Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

function fail(code: AuthFailureCode, message: string, status: number, retryable: boolean): AuthFailure {
  return { ok: false, code, message, status, retryable };
}

/**
 * Map GoTrue's error body to one of our codes and a sentence a coach can act on.
 *
 * This app's rule elsewhere is "print the server's own sentence verbatim", and that rule is right
 * for the DCS gateway, whose refusals were written for a human to read at 2am. GoTrue's are not:
 * "Invalid Refresh Token: Refresh Token Not Found" is a correct thing for a library to say and a
 * useless thing to show a coach at a ground. So these sentences are composed here, from a closed set
 * of codes, and they say what to DO. The raw status is carried in `status` for a bug report.
 *
 * Two body shapes exist in the wild and both are handled: the current
 * `{code, error_code, msg}` and the older OAuth-style `{error, error_description}`.
 */
function describe(status: number, body: unknown): AuthFailure {
  const b = asRecord(body);
  const errorCode = (str(b['error_code']) ?? str(b['error']) ?? '').toLowerCase();
  const msg = (str(b['msg']) ?? str(b['error_description']) ?? str(b['message']) ?? '').toLowerCase();

  if (status === 429 || errorCode.includes('rate_limit')) {
    return fail('rate_limited', 'Too many sign-in attempts. Wait a minute and try again.', status, true);
  }
  if (errorCode === 'email_not_confirmed' || msg.includes('email not confirmed')) {
    return fail(
      'email_not_confirmed',
      'This account exists, but its email address has not been confirmed yet, so it cannot sign in. '
      + 'Whoever set the account up must confirm it in Supabase.',
      status, false,
    );
  }
  if (errorCode === 'user_banned' || msg.includes('banned')) {
    return fail('invalid_credentials', 'This account has been disabled.', status, false);
  }
  if (
    errorCode === 'invalid_credentials'
    || errorCode === 'invalid_grant'
    || msg.includes('invalid login credentials')
  ) {
    // Deliberately does NOT say which of the two was wrong. Telling an attacker "the password was
    // wrong" confirms the email exists on this project.
    return fail(
      'invalid_credentials',
      'That email address and password do not match an account on this project.',
      status, false,
    );
  }
  if (msg.includes('refresh token') || errorCode === 'refresh_token_not_found' || errorCode === 'refresh_token_already_used') {
    return fail(
      'invalid_refresh_token',
      'Your session has ended and could not be renewed. Sign in again.',
      status, false,
    );
  }
  if (status >= 500) {
    return fail('server_error', 'The sign-in service failed. This is not something this phone did wrong.', status, true);
  }
  return fail(
    'unknown',
    `The sign-in service answered ${status} in a shape this app does not recognise.`,
    status, false,
  );
}

function toTokenSet(body: unknown): TokenSet | null {
  const b = asRecord(body);
  const accessToken = str(b['access_token']);
  if (!accessToken) return null;
  const expiresIn = Number(b['expires_in']);
  const user = asRecord(b['user']);
  return {
    accessToken,
    refreshToken: str(b['refresh_token']),
    // Device-relative, on purpose. See the field's own note.
    expiresAt: Number.isFinite(expiresIn) && expiresIn > 0 ? Date.now() + expiresIn * 1000 : null,
    userId: str(user['id']),
    email: str(user['email']),
  };
}

async function tokenGrant(grantType: 'password' | 'refresh_token', body: Record<string, string>): Promise<AuthResult> {
  const base = supabaseUrl();
  const key = supabaseAnonKey();
  if (!base || !key) {
    return fail(
      'not_configured',
      `This build cannot sign in: ${missingSupabaseConfig().join(' and ')} `
      + (missingSupabaseConfig().length > 1 ? 'were' : 'was') + ' not set when it was built.',
      0, false,
    );
  }

  let res: Response;
  try {
    res = await fetch(`${base}/auth/v1/token?grant_type=${grantType}`, {
      method: 'POST',
      headers: {
        // GoTrue identifies the project by `apikey`. The Bearer form is sent too because that is
        // what the official client sends and some deployments key off it. Both carry the ANON key —
        // never the user's token, which does not exist yet on the password grant.
        apikey: key,
        authorization: `Bearer ${key}`,
        'content-type': 'application/json',
        accept: 'application/json',
      },
      body: JSON.stringify(body),
    });
  } catch {
    // NOTE the wording, and how it differs from the API client's. The gateway has an origin
    // allowlist that fails closed, so a fetch rejection THERE is ambiguous between "no network" and
    // "not allowlisted". Supabase's auth endpoint is not origin-allowlisted, so a failure here is
    // genuinely the network or a wrong VITE_SUPABASE_URL. Saying so saves the same hour in the
    // other direction. The caught error is not included: it can carry the request URL.
    return fail(
      'unreachable',
      'Could not reach the sign-in service. Check the connection — nothing was sent.',
      0, true,
    );
  }

  const text = await res.text().catch(() => '');
  let parsed: unknown;
  try { parsed = text ? JSON.parse(text) : {}; } catch { parsed = text; }

  if (!res.ok) return describe(res.status, parsed);

  const tokens = toTokenSet(parsed);
  if (!tokens) {
    // A 200 whose body has no access_token. This is exactly the `{configured:false, url:null}` read
    // as success defect the backend's upload lane was written to end — so it is a failure here, not
    // a session with an empty token.
    return fail(
      'unknown',
      'The sign-in service answered successfully but sent no access token, so there is no session.',
      res.status, false,
    );
  }
  return { ok: true, tokens };
}

/** `POST /auth/v1/token?grant_type=password`. */
export function passwordGrant(email: string, password: string): Promise<AuthResult> {
  return tokenGrant('password', { email: email.trim(), password });
}

/** `POST /auth/v1/token?grant_type=refresh_token`. */
export function refreshGrant(refreshToken: string): Promise<AuthResult> {
  return tokenGrant('refresh_token', { refresh_token: refreshToken });
}
